import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, StickyNote, Save, Search, Check, BellRing, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Notepad-style quick-note dialog scoped to one Job.
 *
 * Header pinned to the top: job number chip + title.
 * Auto-stamped: author name + the current date/time (re-rendered on open).
 * Body: large textarea (autofocus).
 *
 * Single primary action: "Save Note" — always saves to the current job.
 *
 * Optional disclosure: "Also notify a teammate" expands a searchable picker;
 * when a user is chosen, the save also fires an in-app notification + email
 * (best-effort) via the existing notify_to path.
 *
 * Available regardless of job status (todo / in-progress / paused /
 * completed / archived) — by design.
 */
export default function QuickJobNoteDialog({ open, onClose, job, token, currentUser, onSaved }) {
  const { toast } = useToast();
  const [text, setText] = useState('');
  // Optional notify-teammate disclosure. Off by default — the user is already
  // in the job; tapping Save just saves. Notify is a deliberate extra step.
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [pickedUserId, setPickedUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [pastNotes, setPastNotes] = useState([]);

  // Refresh inner state every time the dialog opens for a new job.
  useEffect(() => {
    if (!open) return;
    setText('');
    setNotifyOpen(false);
    setUserSearch('');
    setPickedUserId('');
    // Load co-workers in the same company for the recipient picker.
    if (token) {
      axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setUsers((r.data || []).filter((u) => u.id !== currentUser?.id && !u.deleted_at)))
        .catch(() => setUsers([]));
    }
    // Past notes for this job — fetched from the dedicated endpoint that
    // bypasses the Job pydantic projection (so notes actually come through).
    if (job?.id && token) {
      axios.get(`${API}/jobs/${job.id}/notes`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const notes = (r.data && r.data.notes) || [];
          setPastNotes(notes.slice().reverse());
        })
        .catch(() => setPastNotes([]));
    }
  }, [open, job?.id, token, currentUser?.id]);

  const now = useMemo(() => new Date(), [open]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users.slice(0, 20);
    return users.filter((u) =>
      [u.name, u.email, u.role, u.user_type].some((v) => (v || '').toLowerCase().includes(q))
    ).slice(0, 20);
  }, [userSearch, users]);

  const willNotify = notifyOpen && !!pickedUserId;
  const canSave = text.trim().length >= 1 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        text: text.trim(),
        notify_to: willNotify ? [pickedUserId] : [],
      };
      await axios.post(`${API}/jobs/${job.id}/notes`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: willNotify ? 'Note saved & teammate notified' : 'Note saved',
        description: willNotify
          ? `Saved to ${job.job_number || job.title || 'job'} and notified ${users.find((u) => u.id === pickedUserId)?.name || 'teammate'}.`
          : `Saved to ${job.job_number || job.title || 'job'}.`,
      });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({
        title: 'Could not save note',
        description: e.response?.data?.detail || e.message || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!job) return null;

  const pickedUser = users.find((u) => u.id === pickedUserId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#1A2433] text-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] p-0 overflow-y-auto border-amber-500/30"
        data-testid="quick-note-dialog"
      >
        {/* Pinned header (notepad-style) */}
        <DialogHeader className="px-4 sm:px-6 py-4 bg-gradient-to-r from-amber-500/10 to-amber-700/5 border-b border-amber-500/30">
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-white text-left">
            <StickyNote className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <span className="text-sm font-normal text-slate-300">Add note to</span>
            {job.job_number && (
              <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                {job.job_number}
              </span>
            )}
            <span className="font-semibold break-words min-w-0 flex-1" data-testid="quick-note-job-title">{job.title}</span>
          </DialogTitle>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
            <span>By <strong className="text-white">{currentUser?.name || 'You'}</strong></span>
            <span>{now.toLocaleString()}</span>
            {job.status && (
              <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300 capitalize">
                {String(job.status).replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="p-4 sm:p-6 space-y-4">
          {/* The note body — notepad look */}
          <div className="rounded-md border border-amber-500/30 bg-amber-50/[0.03] p-1">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Jot something down about this job… progress, customer comments, materials needed, anything the next person should know."
              rows={8}
              className="bg-transparent border-0 focus-visible:ring-0 text-white resize-none text-base leading-relaxed"
              autoFocus
              maxLength={4000}
              data-testid="quick-note-text"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(transparent, transparent 27px, rgba(255,255,255,0.05) 28px)',
                lineHeight: '28px',
              }}
            />
            <div className="px-2 pb-1 text-right text-xs text-slate-500">{text.length}/4000</div>
          </div>

          {/* Optional: also notify a teammate. Off by default — Save just saves. */}
          <div data-testid="quick-note-notify-section">
            <button
              type="button"
              onClick={() => setNotifyOpen((v) => !v)}
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition"
              data-testid="quick-note-notify-toggle"
              aria-expanded={notifyOpen}
            >
              <BellRing className={`w-4 h-4 ${notifyOpen ? 'text-blue-400' : 'text-slate-500'}`} />
              <span>Also notify a teammate</span>
              <span className="text-xs text-slate-500">(optional)</span>
              {notifyOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {notifyOpen && (
              <div className="mt-2 space-y-2 border border-blue-500/30 rounded-md p-3 bg-blue-500/[0.04]" data-testid="quick-note-recipient">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search teammates by name, email, role…"
                    className="pl-8 bg-[#0F1923] border-[#2D3748] text-white text-sm"
                    data-testid="quick-note-user-search"
                  />
                </div>
                {pickedUser ? (
                  <div className="flex items-center justify-between bg-blue-500/10 border border-blue-500/40 rounded px-3 py-1.5 text-sm">
                    <div className="min-w-0 flex-1 mr-2">
                      <span className="font-medium block truncate">{pickedUser.name}</span>
                      <span className="text-slate-400 text-xs block truncate">
                        {pickedUser.email} · {pickedUser.role}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPickedUserId(''); setUserSearch(''); }}
                      className="text-slate-400 hover:text-white text-xs flex-shrink-0"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="max-h-44 overflow-y-auto space-y-1" data-testid="quick-note-user-list">
                    {filteredUsers.length === 0 ? (
                      <p className="text-xs text-slate-500 italic px-2 py-1">No teammates match — try a different search.</p>
                    ) : filteredUsers.map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        onClick={() => setPickedUserId(u.id)}
                        className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-blue-500/10 text-left text-sm"
                        data-testid={`quick-note-user-${u.id}`}
                      >
                        <div className="min-w-0 flex-1 mr-2">
                          <span className="font-medium text-white block truncate">{u.name}</span>
                          <span className="text-slate-400 text-xs block truncate">
                            {u.email} · {u.role}{u.user_type ? ` · ${u.user_type}` : ''}
                          </span>
                        </div>
                        <Check className="w-4 h-4 text-blue-400 opacity-0 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Past notes for context */}
          {pastNotes.length > 0 && (
            <details className="text-xs text-slate-400 border-t border-[#2D3748] pt-3">
              <summary className="cursor-pointer hover:text-white" data-testid="quick-note-history-toggle">
                Past notes on this job ({pastNotes.length})
              </summary>
              <div className="mt-2 space-y-2 max-h-44 overflow-y-auto pr-1" data-testid="quick-note-history">
                {pastNotes.map((n) => (
                  <div key={n.id} className="bg-[#0F1923] rounded p-2 border-l-2 border-amber-500/40">
                    <div className="flex justify-between gap-2 mb-1 text-[11px] text-slate-500">
                      <span><strong className="text-amber-300">{n.user_name || 'Someone'}</strong></span>
                      <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-slate-200 whitespace-pre-wrap text-sm">{n.text}</p>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Footer — single save action */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-[#2D3748]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="quick-note-cancel"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
              data-testid="quick-note-save"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Note</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
